import type { ComponentChildren } from 'preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import { t } from '../../i18n/index.js';
import { PieceBox, drawField, useTetraxLoop } from './TetraxBoard.js';
import { TETRAX_H, TETRAX_NEXT_SHOWN, pendingGarbage, type TetraxEvents, type TetraxState } from '../../../shared/games/tetrax/index.js';

export const CELL = 30;

/**
 * The player's own Tetrax board with the hold, the next pieces and the numbers (2026-10-03), used by
 * the solo game and the duel. The board is drawn on every animation frame; the numbers beside it
 * are refreshed when something changes.
 */
export function TetraxStage(props: {
  state: { current: TetraxState };
  active: boolean;
  overlay: ComponentChildren;
  dim?: boolean;
  onEvents?: (e: TetraxEvents) => void;
  onChange?: () => void;
  onPause?: () => void;
  /** Below the numbers on the left (e.g. the best score). */
  extra?: ComponentChildren;
  /** Right of the board (the opponent in a duel). */
  side?: ComponentChildren;
  /** The duel shows the garbage coming in. */
  showGarbage?: boolean;
}) {
  const canvas = useRef<HTMLCanvasElement>(null);
  const [, setVersion] = useState(0);
  const pending = useRef(false);
  const refresh = () => {
    if (pending.current) return;
    pending.current = true;
    requestAnimationFrame(() => {
      pending.current = false;
      setVersion((v) => v + 1);
    });
  };
  const draw = () => canvas.current && drawField(canvas.current, props.state.current, CELL, { dim: props.dim });

  useTetraxLoop({
    state: props.state,
    active: props.active,
    frame: draw,
    onEvents: props.onEvents,
    onChange: () => {
      refresh();
      props.onChange?.();
    },
    onPause: props.onPause,
  });
  // Not running (before the start, paused, over): drawn when shown.
  useEffect(() => {
    draw();
  });

  const s = props.state.current;
  const incoming = pendingGarbage(s);
  return (
    <div class="tetrax-stage">
      <div class="tetrax-side tetrax-left">
        <div class="tetrax-box">
          <div class="tetrax-label">{t.tetrax.hold}</div>
          <PieceBox type={s.hold} dim={s.holdUsed} />
        </div>
        <dl class="tetrax-stats">
          <dt>{t.tetrax.score}</dt>
          <dd>{s.score.toLocaleString('fi-FI')}</dd>
          <dt>{t.tetrax.lines}</dt>
          <dd>{s.lines}</dd>
          <dt>{t.tetrax.level}</dt>
          <dd>{s.level}</dd>
        </dl>
        {props.extra}
      </div>
      {props.showGarbage && (
        <div class="tetrax-meter" title={`${t.tetrax.incoming}: ${incoming}`} aria-label={`${t.tetrax.incoming}: ${incoming}`}>
          <div class="tetrax-meter-fill" style={{ height: `${Math.min(100, (incoming / TETRAX_H) * 100)}%` }} />
        </div>
      )}
      <div class="tetrax-well">
        <canvas ref={canvas} class="tetrax-canvas" role="img" aria-label={t.tetrax.boardLabel} />
        {props.overlay && <div class="tetrax-overlay">{props.overlay}</div>}
      </div>
      <div class="tetrax-side tetrax-right">
        <div class="tetrax-box">
          <div class="tetrax-label">{t.tetrax.next}</div>
          {s.queue.slice(0, TETRAX_NEXT_SHOWN).map((type, i) => (
            <PieceBox key={i} type={type} cell={i === 0 ? 18 : 14} />
          ))}
        </div>
      </div>
      {props.side}
    </div>
  );
}
